// These are newly governed fields agreed with the Speedy integration workstream.
// Their presence alone is not cryptographic checkout validation. The importer
// verifies the signed selection and the provider's current staffed office.
export const selectionKeys = [
    'erp_speedy_version',
    'erp_speedy_destination',
    'erp_speedy_office_id',
    'erp_speedy_selection',
];
function record(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('INVALID_RESPONSE');
    }
    return value;
}
function text(value) {
    if (typeof value !== 'string')
        throw new Error('INVALID_RESPONSE');
    return value;
}
function nullableText(value) {
    return value === null ? null : text(value);
}
function positiveId(value) {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 1) {
        throw new Error('INVALID_RESPONSE');
    }
    return value;
}
function count(value) {
    if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
        throw new Error('INVALID_RESPONSE');
    }
    return value;
}
function list(value, limit) {
    if (!Array.isArray(value) || value.length > limit)
        throw new Error('INVALID_RESPONSE');
    return value;
}
export function parseCheckoutAddress(value) {
    if (value === null || value === undefined)
        return null;
    const row = record(value);
    const address1 = text(row.address1).trim();
    const city = text(row.city).trim();
    const zip = text(row.zip);
    if (!address1 || !city || !/^\d{4}$/.test(zip) || row.countryCode !== 'BG')
        throw new Error('INVALID_RESPONSE');
    return { address1, city, zip, countryCode: 'BG' };
}
export function parseCart(value) {
    const data = record(value);
    if (typeof data.requires_shipping !== 'boolean')
        throw new Error('INVALID_RESPONSE');
    const attributes = record(data.attributes);
    return {
        item_count: count(data.item_count),
        requires_shipping: data.requires_shipping,
        currency: text(data.currency),
        items: list(data.items, 1_000).map((item) => {
            const row = record(item);
            return { key: text(row.key), quantity: count(row.quantity) };
        }),
        attributes: Object.fromEntries(Object.entries(attributes).map(([key, value]) => [key, text(value)])),
    };
}
export function parseSites(value) {
    const data = record(value);
    if (data.version !== 1)
        throw new Error('INVALID_RESPONSE');
    return list(data.sites, 50).map((value) => {
        const row = record(value);
        return {
            id: positiveId(row.id),
            name: text(row.name),
            nameEn: nullableText(row.nameEn),
            postCode: nullableText(row.postCode),
        };
    });
}
function parseOffice(value) {
    const row = record(value);
    if (row.type !== 'OFFICE' || row.pickUpAllowed !== true) {
        throw new Error('INVALID_RESPONSE');
    }
    return {
        id: positiveId(row.id),
        name: text(row.name),
        nameEn: nullableText(row.nameEn),
        siteId: positiveId(row.siteId),
        siteName: text(row.siteName),
        address: text(row.address),
        type: 'OFFICE',
        pickUpAllowed: true,
        checkoutAddress: parseCheckoutAddress(row.checkoutAddress),
    };
}
export function parseOffices(value, siteId) {
    const data = record(value);
    if (data.version !== 1)
        throw new Error('INVALID_RESPONSE');
    const offices = list(data.offices, 100).map(parseOffice);
    if (offices.some((office) => office.siteId !== siteId))
        throw new Error('INVALID_RESPONSE');
    return offices;
}
export function parseSelection(value, mode, office) {
    const data = record(value);
    if (data.version !== 1)
        throw new Error('INVALID_RESPONSE');
    const destination = record(data.destination);
    const attributes = record(data.attributes);
    const checkoutAddress = parseCheckoutAddress(data.checkoutAddress);
    const parsed = Object.fromEntries(selectionKeys.map((key) => [key, text(attributes[key])]));
    if (destination.type !== mode ||
        parsed.erp_speedy_version !== '1' ||
        parsed.erp_speedy_destination !== mode ||
        parsed.erp_speedy_selection.length === 0 ||
        parsed.erp_speedy_selection.length > 8_192) {
        throw new Error('INVALID_RESPONSE');
    }
    if (mode === 'ADDRESS') {
        if (parsed.erp_speedy_office_id !== '')
            throw new Error('INVALID_RESPONSE');
        if (checkoutAddress !== null)
            throw new Error('INVALID_RESPONSE');
        return { version: 1, destination: { type: 'ADDRESS' }, attributes: parsed, checkoutAddress };
    }
    const selected = parseOffice(destination.office);
    if (!office ||
        selected.id !== office.id ||
        selected.siteId !== office.siteId ||
        selected.name !== office.name ||
        selected.siteName !== office.siteName ||
        selected.address !== office.address ||
        parsed.erp_speedy_office_id !== String(office.id)) {
        throw new RequestError('SPEEDY_OFFICE_INVALID');
    }
    if (JSON.stringify(checkoutAddress) !== JSON.stringify(office.checkoutAddress ?? null))
        throw new RequestError('SPEEDY_OFFICE_INVALID');
    return {
        version: 1,
        destination: { type: 'OFFICE', office: { ...selected, checkoutAddress } },
        attributes: parsed,
        checkoutAddress,
    };
}
export function sameAttributes(cart, attributes) {
    return selectionKeys.every((key) => (cart.attributes[key] ?? '') === attributes[key]);
}
export function cartFingerprint(cart) {
    return JSON.stringify([cart.currency, cart.requires_shipping, cart.items]);
}
// Display labels only. Native discounted-before-tax shipping tiers and Payflow
// remain the financial/payment authority. No AJAX cart total selects a tier.
export function priceLabels(mode) {
    return mode === 'OFFICE'
        ? { prepaid: '3,00 €', cod: '4,50 €' }
        : { prepaid: '4,20 €', cod: '5,70 €' };
}
export const clearSelection = {
    erp_speedy_version: '',
    erp_speedy_destination: '',
    erp_speedy_office_id: '',
    erp_speedy_selection: '',
};
const errors = {
    SPEEDY_OFFICE_ADDRESS_UNAVAILABLE: 'Адресът на този офис не е достатъчно уточнен за плащане. Изберете друг офис или доставка до адрес.',
    CHECKOUT_PREFILL_FAILED: 'Адресът за доставка не можа да бъде потвърден. Останете в количката и опитайте отново.',
    CHECKOUT_PREFILL_UNCERTAIN: 'Адресът за доставка все още не е потвърден. Не продължавайте към плащане.',
    SPEEDY_OFFICE_INVALID: 'Този офис вече не е наличен. Изберете отново офис на Speedy.',
    SPEEDY_SITE_QUERY_REQUIRED: 'Въведете поне 2 букви или пощенски код от 4 цифри.',
    SHOPIFY_SPEEDY_PROXY_INVALID: 'Изборът на доставка не може да бъде потвърден. Опитайте отново.',
    SHOPIFY_SPEEDY_CONNECTION_UNAVAILABLE: 'Офисите на Speedy временно не са достъпни. Опитайте отново.',
    SHOPIFY_SPEEDY_ROUTE_REQUIRED: 'Доставката със Speedy временно не е достъпна. Опитайте отново.',
    SHOPIFY_SPEEDY_SELECTION_INVALID: 'Изборът на доставка е невалиден. Изберете отново.',
    CART_CHANGED: 'Количката е променена. Проверете доставката и натиснете „Продължи“ отново.',
    CART_EMPTY: 'Количката е празна. Добавете продукт, преди да продължите.',
    CURRENCY_UNSUPPORTED: 'Доставката за България се предлага в евро. Проверете избраната държава и валута.',
};
class RequestError extends Error {
    code;
    constructor(code) {
        super(code);
        this.code = code;
    }
}
async function request(url, body, signal) {
    const timeout = AbortSignal.timeout(12_000);
    const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;
    const response = await fetch(url, {
        method: body === undefined ? 'GET' : 'POST',
        credentials: 'same-origin',
        headers: {
            Accept: 'application/json',
            ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        },
        cache: 'no-store',
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
        signal: combined,
    });
    const data = await response.json();
    if (!response.ok) {
        const failure = record(data);
        const code = typeof failure.code === 'string' ? failure.code : 'REQUEST_FAILED';
        throw new RequestError(code);
    }
    return data;
}
function required(dialog, selector) {
    const element = dialog.querySelector(selector);
    if (!element)
        throw new Error('POPUP_MARKUP_INVALID');
    return element;
}
const deliveryFields = 'delivery { addresses { id selected oneTimeUse address { ... on CartDeliveryAddress { firstName lastName phone company address1 address2 city zip countryCode } } } }';
// This edits the existing guest/Online Store cart only. Its key stays in memory;
// the storage marker contains public office fields/address IDs and a key digest.
// Never recreate cart lines, write buyerIdentity, or save a customer/billing address.
export class CartOfficePrefill {
    shop;
    storage;
    transport;
    storageKey = 'erp-speedy-office-prefill-v1';
    constructor(shop, storage, transport = (input, init) => fetch(input, init)) {
        this.shop = shop;
        this.storage = storage;
        this.transport = transport;
    }
    hasOwned() {
        try {
            return this.storage.getItem(this.storageKey) !== null;
        }
        catch {
            // Storage failure must enter the guarded popup, never bypass it.
            return true;
        }
    }
    async identity(cart) {
        const token = text(record(cart).token);
        if (!token.includes('?key='))
            throw new RequestError('CHECKOUT_PREFILL_FAILED');
        const id = token.startsWith('gid://shopify/Cart/') ? token : `gid://shopify/Cart/${token}`;
        const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(id));
        return {
            id,
            digest: [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join(''),
        };
    }
    async operation(query, variables, mutation) {
        if (!['hvgjry-ig.myshopify.com', '0x8sf1-sz.myshopify.com', 'smp3z9-v0.myshopify.com'].includes(this.shop))
            throw new RequestError('CHECKOUT_PREFILL_FAILED');
        const response = await this.transport(`https://${this.shop}/api/2026-07/graphql.json`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ query, variables }),
            signal: AbortSignal.timeout(12_000),
        });
        const body = record(await response.json());
        if (!response.ok || body.errors)
            throw new RequestError('CHECKOUT_PREFILL_FAILED');
        const data = record(body.data);
        const payload = mutation ? record(data[mutation]) : undefined;
        if (payload && list(payload.userErrors, 100).length)
            throw new RequestError('CHECKOUT_PREFILL_REJECTED');
        const cart = record(payload ? payload.cart : data.cart);
        return list(record(cart.delivery).addresses, 20).map((value) => {
            const row = record(value);
            if (typeof row.selected !== 'boolean' || typeof row.oneTimeUse !== 'boolean')
                throw new Error('INVALID_RESPONSE');
            return {
                id: text(row.id),
                selected: row.selected,
                oneTimeUse: row.oneTimeUse,
                address: record(row.address),
            };
        });
    }
    read(id) {
        return this.operation(`query($id:ID!){cart(id:$id){${deliveryFields}}}`, { id });
    }
    matches(row, address) {
        return (row.oneTimeUse &&
            row.address.address1 === address.address1 &&
            row.address.city === address.city &&
            row.address.zip === address.zip &&
            row.address.countryCode === address.countryCode &&
            (row.address.address2 === '' || row.address.address2 === null));
    }
    async apply(cart, address) {
        await this.restore(cart);
        const { id, digest } = await this.identity(cart);
        const before = await this.read(id);
        const previous = before.find((row) => row.selected);
        const owned = {
            cart: digest,
            beforeIds: before.map((row) => row.id),
            ...(previous ? { previousId: previous.id } : {}),
            address,
        };
        // Persist the bounded ownership intent before the POST, so an interrupted
        // response cannot lead to a second Add or an untracked selected office.
        this.storage.setItem(this.storageKey, JSON.stringify(owned));
        const contact = previous
            ? Object.fromEntries(['firstName', 'lastName', 'phone', 'company'].flatMap((key) => typeof previous.address[key] === 'string' && previous.address[key]
                ? [[key, previous.address[key]]]
                : []))
            : {};
        let after;
        try {
            after = await this.operation(`mutation($id:ID!,$addresses:[CartSelectableAddressInput!]!){cartDeliveryAddressesAdd(cartId:$id,addresses:$addresses){userErrors{code}cart{${deliveryFields}}}}`, {
                id,
                addresses: [
                    {
                        selected: true,
                        oneTimeUse: true,
                        address: { deliveryAddress: { ...contact, ...address, address2: '' } },
                    },
                ],
            }, 'cartDeliveryAddressesAdd');
        }
        catch (error) {
            if (error instanceof RequestError && error.code === 'CHECKOUT_PREFILL_REJECTED') {
                this.storage.removeItem(this.storageKey);
                throw new RequestError('CHECKOUT_PREFILL_FAILED');
            }
            // Exactly one readback; never automatically retry an accepted/unknown Add.
            after = await this.read(id);
        }
        const candidates = after.filter((row) => !owned.beforeIds.includes(row.id) && row.selected && this.matches(row, address));
        if (candidates.length !== 1)
            throw new RequestError('CHECKOUT_PREFILL_UNCERTAIN');
        owned.id = candidates[0].id;
        this.storage.setItem(this.storageKey, JSON.stringify(owned));
    }
    async restore(cart) {
        const raw = this.storage.getItem(this.storageKey);
        if (raw === null)
            return;
        const owned = JSON.parse(raw);
        const { id, digest } = await this.identity(cart);
        if (owned.cart !== digest) {
            this.storage.removeItem(this.storageKey);
            return;
        }
        let rows = await this.read(id);
        if (!owned.id) {
            const candidates = rows.filter((row) => !owned.beforeIds.includes(row.id) && this.matches(row, owned.address));
            if (candidates.length !== 1)
                throw new RequestError('CHECKOUT_PREFILL_UNCERTAIN');
            owned.id = candidates[0].id;
            this.storage.setItem(this.storageKey, JSON.stringify(owned));
        }
        const office = rows.find((row) => row.id === owned.id);
        const preserveSelected = rows.some((row) => row.selected && row.id !== owned.id);
        if (office && !this.matches(office, owned.address)) {
            // The buyer changed the postal address in checkout. Preserve that edit.
            this.storage.removeItem(this.storageKey);
            return;
        }
        if (office) {
            try {
                rows = await this.operation(`mutation($id:ID!,$ids:[ID!]!){cartDeliveryAddressesRemove(cartId:$id,addressIds:$ids){userErrors{code}cart{${deliveryFields}}}}`, { id, ids: [owned.id] }, 'cartDeliveryAddressesRemove');
            }
            catch {
                rows = await this.read(id);
            }
            if (rows.some((row) => row.id === owned.id))
                throw new RequestError('CHECKOUT_PREFILL_UNCERTAIN');
        }
        const previous = rows.find((row) => row.id === owned.previousId);
        if (previous && !previous.selected && !preserveSelected) {
            try {
                rows = await this.operation(`mutation($id:ID!,$addresses:[CartSelectableAddressUpdateInput!]!){cartDeliveryAddressesUpdate(cartId:$id,addresses:$addresses){userErrors{code}cart{${deliveryFields}}}}`, { id, addresses: [{ id: previous.id, selected: true }] }, 'cartDeliveryAddressesUpdate');
            }
            catch {
                rows = await this.read(id);
            }
            if (!rows.some((row) => row.id === previous.id && row.selected))
                throw new RequestError('CHECKOUT_PREFILL_UNCERTAIN');
        }
        this.storage.removeItem(this.storageKey);
    }
}
export class SpeedyOfficePopup {
    dialog;
    config;
    prefill;
    mode;
    selectedOffice;
    offices = [];
    intent;
    approval;
    approvalTimer;
    handoffClosing = false;
    busy = false;
    searchGeneration = 0;
    searchController;
    fieldset;
    siteQuery;
    sites;
    officeFilter;
    officeList;
    next;
    status;
    error;
    constructor(dialog, config) {
        this.dialog = dialog;
        this.config = config;
        this.prefill = new CartOfficePrefill(config.shop, {
            getItem: (key) => localStorage.getItem(key),
            setItem: (key, value) => localStorage.setItem(key, value),
            removeItem: (key) => localStorage.removeItem(key),
        });
        this.fieldset = required(dialog, '[data-speedy-fields]');
        this.siteQuery = required(dialog, '[data-speedy-site-query]');
        this.sites = required(dialog, '[data-speedy-sites]');
        this.officeFilter = required(dialog, '[data-speedy-office-filter]');
        this.officeList = required(dialog, '[data-speedy-offices]');
        this.next = required(dialog, '[data-speedy-continue]');
        this.status = required(dialog, '[data-speedy-status]');
        this.error = required(dialog, '[data-speedy-error]');
        document.addEventListener('submit', this.onSubmit, true);
        document.addEventListener('cart:updated', () => this.clearApproval());
        document.addEventListener('click', (event) => {
            if (event.isTrusted &&
                event.target instanceof Element &&
                event.target.closest('button[name="checkout"]'))
                this.clearApproval();
        }, true);
        for (const type of ['input', 'change', 'keydown']) {
            document.addEventListener(type, (event) => {
                if (!this.approval || !(event.target instanceof Element))
                    return;
                if (event instanceof KeyboardEvent && event.key !== 'Enter')
                    return;
                if (this.approval.form.contains(event.target) ||
                    event.target.matches('[name="country_code"], [name="country"]'))
                    this.clearApproval();
            }, true);
        }
        dialog.addEventListener('change', this.onMode);
        dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            if (!this.busy)
                void this.close();
        });
        dialog.addEventListener('close', () => this.onClose());
        dialog.addEventListener('click', (event) => {
            if (event.target !== dialog || this.busy)
                return;
            const box = dialog.getBoundingClientRect();
            if (event.clientX < box.left ||
                event.clientX > box.right ||
                event.clientY < box.top ||
                event.clientY > box.bottom)
                void this.close();
        });
        dialog.addEventListener('keydown', (event) => {
            // Keep the underlying Impulse drawer open while handling this dialog.
            if (event.key === 'Escape')
                event.stopPropagation();
            if (event.key === 'Tab') {
                const focusable = [
                    ...dialog.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), [href], [tabindex="0"]'),
                ].filter((element) => element.getClientRects().length > 0);
                const first = focusable[0];
                const last = focusable.at(-1);
                if (first && last && event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                }
                else if (first && last && !event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        });
        required(dialog, '[data-speedy-close]').addEventListener('click', () => void this.close());
        required(dialog, '[data-speedy-search]').addEventListener('click', () => {
            void this.searchSites();
        });
        this.siteQuery.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                void this.searchSites();
            }
        });
        this.sites.addEventListener('change', () => {
            void this.loadOffices();
        });
        this.officeFilter.addEventListener('input', () => this.renderOffices());
        required(dialog, '[data-speedy-office-search]').addEventListener('click', () => {
            void this.loadOffices(true);
        });
        this.officeFilter.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                void this.loadOffices(true);
            }
        });
        this.next.addEventListener('click', () => {
            void this.continue();
        });
    }
    onSubmit = (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !['CartPageForm', 'CartDrawerForm'].includes(form.id))
            return;
        const button = event.submitter ?? form.querySelector('button[name="checkout"]');
        if (!(button instanceof HTMLButtonElement) || button.name !== 'checkout')
            return;
        if (this.consumeApproval(form, button))
            return;
        if (this.config.country !== 'BG' && !this.config.hasSelection && !this.prefill.hasOwned())
            return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.dialog.open || this.busy) {
            this.dialog.focus();
            return;
        }
        this.intent = { form, button };
        void this.open();
    };
    async open() {
        this.reset();
        this.dialog.showModal();
        this.setBusy(true, 'Проверяваме количката…');
        try {
            const rawCart = await request(this.config.cartUrl);
            const cart = parseCart(rawCart);
            await this.prefill.restore(rawCart);
            if (cart.item_count === 0)
                throw new RequestError('CART_EMPTY');
            if (this.config.country !== 'BG' || !cart.requires_shipping) {
                if (selectionKeys.some((key) => Boolean(cart.attributes[key]))) {
                    await this.saveAttributes(clearSelection);
                }
                this.handoff();
                return;
            }
            if (cart.currency !== 'EUR')
                throw new RequestError('CURRENCY_UNSUPPORTED');
            if (this.intent)
                this.intent.fingerprint = cartFingerprint(cart);
            this.setBusy(false, 'Изберете как да получите поръчката.');
            required(this.dialog, '[name="erp-speedy-mode"]').focus();
        }
        catch (error) {
            this.showError(error);
            this.setBusy(false);
        }
    }
    onMode = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.name !== 'erp-speedy-mode')
            return;
        this.mode = input.value === 'OFFICE' ? 'OFFICE' : 'ADDRESS';
        if (this.mode === 'ADDRESS') {
            this.searchController?.abort();
            this.searchGeneration += 1;
            this.status.textContent = '';
        }
        required(this.dialog, '[data-speedy-office-picker]').hidden =
            this.mode !== 'OFFICE';
        const labels = priceLabels(this.mode);
        required(this.dialog, '[data-speedy-prepaid]').textContent = labels.prepaid;
        required(this.dialog, '[data-speedy-cod]').textContent = labels.cod;
        required(this.dialog, '[data-speedy-prices]').hidden = false;
        this.clearError();
        this.updateContinue();
        if (this.mode === 'OFFICE')
            this.siteQuery.focus();
    };
    async searchSites() {
        const query = this.siteQuery.value.trim();
        if (query.length < 2 || (/^\d+$/.test(query) && !/^\d{4}$/.test(query))) {
            this.showError(new RequestError('SPEEDY_SITE_QUERY_REQUIRED'));
            return;
        }
        const generation = this.startSearch();
        this.clearOffice();
        this.sites.replaceChildren(new Option('Изберете населено място', ''));
        this.status.textContent = 'Търсим населено място…';
        try {
            const params = new URLSearchParams(/^\d{4}$/.test(query) ? { postCode: query } : { name: query });
            const sites = parseSites(await request(`${this.config.proxyBase}/sites?${params.toString()}`, undefined, this.searchController?.signal));
            if (generation !== this.searchGeneration || !this.dialog.open || this.mode !== 'OFFICE')
                return;
            for (const site of sites)
                this.sites.add(new Option(`${site.name}${site.postCode ? ` · ${site.postCode}` : ''}`, String(site.id)));
            this.status.textContent = sites.length
                ? 'Изберете населено място. При много резултати уточнете търсенето.'
                : 'Няма резултати. Проверете името или пощенския код.';
            if (sites.length)
                this.sites.focus();
        }
        catch (error) {
            if (generation === this.searchGeneration && this.dialog.open)
                this.showError(error);
        }
    }
    async loadOffices(searchByName = false) {
        const name = searchByName ? this.officeFilter.value.trim() : '';
        const generation = this.startSearch();
        this.clearOffice();
        this.officeFilter.value = name;
        if (!this.sites.value)
            return;
        const siteId = Number(this.sites.value);
        this.status.textContent = 'Зареждаме офисите…';
        try {
            const params = new URLSearchParams({ siteId: String(siteId) });
            if (name)
                params.set('name', name);
            const offices = parseOffices(await request(`${this.config.proxyBase}/offices?${params.toString()}`, undefined, this.searchController?.signal), siteId);
            if (generation !== this.searchGeneration || !this.dialog.open || this.mode !== 'OFFICE')
                return;
            this.offices = offices;
            this.renderOffices();
        }
        catch (error) {
            if (generation === this.searchGeneration && this.dialog.open)
                this.showError(error);
        }
    }
    renderOffices() {
        const query = this.officeFilter.value.trim().toLocaleLowerCase('bg');
        const rows = this.offices.filter((office) => `${office.name} ${office.nameEn ?? ''} ${office.address}`
            .toLocaleLowerCase('bg')
            .includes(query));
        this.officeList.replaceChildren();
        for (const office of rows) {
            const label = document.createElement('label');
            label.className = 'erp-speedy-office';
            const radio = document.createElement('input');
            radio.type = 'radio';
            radio.name = 'erp-speedy-office';
            radio.value = String(office.id);
            radio.checked = office.id === this.selectedOffice?.id;
            radio.addEventListener('change', () => {
                this.selectedOffice = office;
                this.clearError();
                this.updateContinue();
            });
            const title = document.createElement('strong');
            title.textContent = office.name;
            const address = document.createElement('span');
            address.textContent = office.address;
            const details = document.createElement('span');
            details.append(title, address);
            label.append(radio, details);
            this.officeList.append(label);
        }
        if (this.selectedOffice && !rows.some((row) => row.id === this.selectedOffice?.id))
            this.selectedOffice = undefined;
        this.status.textContent = rows.length
            ? 'Изберете офис с обслужване от служител. Показваме до 100 резултата; търсете по име или улица, ако офисът липсва.'
            : 'Няма налични офиси за това търсене. Проверете името, изберете друго населено място или доставка до адрес.';
        this.updateContinue();
    }
    async continue() {
        if (!this.mode ||
            !this.intent?.fingerprint ||
            this.busy ||
            (this.mode === 'OFFICE' && !this.selectedOffice))
            return;
        this.setBusy(true, 'Потвърждаваме доставката…');
        this.clearError();
        try {
            const rawCart = await request(this.config.cartUrl);
            await this.prefill.restore(rawCart);
            const cart = parseCart(rawCart);
            if (cartFingerprint(cart) !== this.intent.fingerprint) {
                this.intent.fingerprint = cartFingerprint(cart);
                throw new RequestError('CART_CHANGED');
            }
            if (!cart.requires_shipping || cart.item_count === 0)
                throw new RequestError('CART_EMPTY');
            if (cart.currency !== 'EUR')
                throw new RequestError('CURRENCY_UNSUPPORTED');
            const body = this.mode === 'ADDRESS'
                ? { type: 'ADDRESS' }
                : { type: 'OFFICE', officeId: this.selectedOffice?.id };
            const selection = parseSelection(await request(`${this.config.proxyBase}/selection`, body), this.mode, this.selectedOffice);
            if (this.mode === 'OFFICE' && !selection.checkoutAddress)
                throw new RequestError('SPEEDY_OFFICE_ADDRESS_UNAVAILABLE');
            const updated = await this.saveAttributes(selection.attributes);
            if (cartFingerprint(updated) !== this.intent.fingerprint) {
                this.intent.fingerprint = cartFingerprint(updated);
                throw new RequestError('CART_CHANGED');
            }
            if (this.mode === 'OFFICE') {
                await this.prefill.apply(await request(this.config.cartUrl), selection.checkoutAddress);
                const current = parseCart(await request(this.config.cartUrl));
                if (cartFingerprint(current) !== this.intent.fingerprint ||
                    !sameAttributes(current, selection.attributes))
                    throw new RequestError('CART_CHANGED');
            }
            this.handoff();
        }
        catch (error) {
            if (error instanceof RequestError && error.code === 'SPEEDY_OFFICE_INVALID')
                this.clearOffice();
            this.showError(error);
            this.setBusy(false);
        }
    }
    async saveAttributes(attributes) {
        let cart;
        try {
            cart = parseCart(await request(this.config.updateUrl, { attributes }));
        }
        catch {
            // An ambiguous POST never triggers an automatic second write. Read back
            // once, then continue only if all four requested attributes are present.
            cart = parseCart(await request(this.config.cartUrl));
        }
        if (!sameAttributes(cart, attributes))
            throw new RequestError('SHOPIFY_SPEEDY_SELECTION_INVALID');
        return cart;
    }
    handoff() {
        const intent = this.intent;
        if (!intent || !intent.form.isConnected || !intent.button.isConnected)
            throw new Error('CHECKOUT_FORM_CHANGED');
        this.clearApproval();
        this.approval = {
            form: intent.form,
            button: intent.button,
            country: this.dialog.dataset.country ?? this.config.country,
            formState: this.checkoutFormState(intent.form),
            expiresAt: Date.now() + 30_000,
        };
        this.approvalTimer = setTimeout(() => this.clearApproval(), 30_000);
        this.setBusy(false);
        this.handoffClosing = true;
        this.dialog.close();
        try {
            // Run the original theme handlers, including terms/quantity guards. Never
            // use form.submit(), a fabricated /checkout URL or a payment replacement.
            intent.form.requestSubmit(intent.button);
        }
        catch (error) {
            this.clearApproval();
            throw error;
        }
    }
    checkoutFormState(form) {
        return JSON.stringify({
            action: form.getAttribute('action'),
            method: form.method,
            fields: [...form.elements].flatMap((element) => {
                if (!(element instanceof HTMLInputElement) &&
                    !(element instanceof HTMLSelectElement) &&
                    !(element instanceof HTMLTextAreaElement))
                    return [];
                if (element.name === 'checkout')
                    return [];
                return [
                    [element.name, element.value, element instanceof HTMLInputElement && element.checked],
                ];
            }),
        });
    }
    consumeApproval(form, button) {
        const approval = this.approval;
        if (!approval)
            return false;
        // Only the submit that actually reaches this listener consumes approval.
        // Earlier capture handlers may stop it and replay it asynchronously.
        this.clearApproval();
        return (form === approval.form &&
            button === approval.button &&
            button.form === form &&
            form.isConnected &&
            button.isConnected &&
            !button.disabled &&
            Date.now() < approval.expiresAt &&
            (this.dialog.dataset.country ?? this.config.country) === approval.country &&
            this.checkoutFormState(form) === approval.formState);
    }
    clearApproval() {
        if (this.approvalTimer !== undefined)
            clearTimeout(this.approvalTimer);
        this.approvalTimer = undefined;
        this.approval = undefined;
    }
    startSearch() {
        this.searchController?.abort();
        this.searchController = new AbortController();
        this.searchGeneration += 1;
        this.clearError();
        return this.searchGeneration;
    }
    clearOffice() {
        this.selectedOffice = undefined;
        this.offices = [];
        this.officeFilter.value = '';
        this.officeList.replaceChildren();
        this.updateContinue();
    }
    reset() {
        this.clearApproval();
        this.mode = undefined;
        this.clearOffice();
        this.clearError();
        this.siteQuery.value = '';
        this.sites.replaceChildren(new Option('Изберете населено място', ''));
        for (const radio of this.dialog.querySelectorAll('[name="erp-speedy-mode"]'))
            radio.checked = false;
        required(this.dialog, '[data-speedy-office-picker]').hidden = true;
        required(this.dialog, '[data-speedy-prices]').hidden = true;
    }
    setBusy(busy, status = '') {
        this.busy = busy;
        this.fieldset.disabled = busy;
        this.dialog.setAttribute('aria-busy', String(busy));
        required(this.dialog, '[data-speedy-close]').disabled = busy;
        this.status.textContent = status;
        this.updateContinue();
    }
    updateContinue() {
        this.next.disabled =
            this.busy ||
                !this.mode ||
                !this.intent?.fingerprint ||
                (this.mode === 'OFFICE' && !this.selectedOffice);
    }
    clearError() {
        this.error.hidden = true;
        this.error.textContent = '';
    }
    showError(error) {
        this.clearApproval();
        const code = error instanceof RequestError ? error.code : 'REQUEST_FAILED';
        this.error.textContent =
            errors[code] ??
                'Връзката временно не е достъпна. Опитайте отново. Доставката не е потвърдена.';
        this.error.hidden = false;
        this.status.textContent = '';
    }
    async close() {
        if (this.busy)
            return;
        this.clearApproval();
        if (this.prefill.hasOwned()) {
            this.setBusy(true, 'Възстановяваме адреса за доставка…');
            try {
                await this.prefill.restore(await request(this.config.cartUrl));
            }
            catch (error) {
                this.showError(error);
                this.setBusy(false);
                return;
            }
            this.setBusy(false);
        }
        this.dialog.close();
    }
    onClose() {
        if (!this.handoffClosing)
            this.clearApproval();
        this.handoffClosing = false;
        this.searchController?.abort();
        this.searchGeneration += 1;
        this.intent = undefined;
    }
}
if (typeof document !== 'undefined') {
    const dialog = document.querySelector('[data-speedy-popup]');
    if (dialog?.dataset.country &&
        dialog.dataset.cartUrl &&
        dialog.dataset.updateUrl &&
        dialog.dataset.proxyBase &&
        dialog.dataset.shop) {
        new SpeedyOfficePopup(dialog, {
            country: dialog.dataset.country,
            cartUrl: dialog.dataset.cartUrl,
            updateUrl: dialog.dataset.updateUrl,
            proxyBase: dialog.dataset.proxyBase,
            hasSelection: dialog.dataset.hasSelection === 'true',
            shop: dialog.dataset.shop,
        });
    }
}
